import { useState } from "react";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Download, FileText, Code, BookOpen, Loader2, FileDown,
  Gauge, Globe
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DOCUMENTS = [
  {
    id: "source-code-pdf",
    title: "Code Source Complet (PDF)",
    description: "Document PDF contenant l'integralite du code source backend (FastAPI) et frontend (React) - 418 Ko",
    icon: Code,
    color: "text-rose-400",
    bg: "bg-rose-500/15",
    border: "border-rose-500/30",
    url: `${API}/downloads/source-code-pdf`,
    filename: "CMES_PHARMA_INTEL_CODE_SOURCE_COMPLET.pdf",
    format: "PDF",
  },
  {
    id: "guide-pdf",
    title: "Guide des Fonctionnalites",
    description: "Document PDF complet listant toutes les fonctionnalites de CMES INTEL-STOCK (10 sections, 230 Ko)",
    icon: BookOpen,
    color: "text-sky-400",
    bg: "bg-sky-500/15",
    border: "border-sky-500/30",
    url: `${API}/downloads/guide-fonctionnalites/pdf`,
    filename: "CMES_Pharma_Intel_Guide_Fonctionnalites.pdf",
    format: "PDF",
  },
  {
    id: "documentation",
    title: "Documentation Technique",
    description: "Document PDF avec l'architecture, les endpoints API, les collections MongoDB et les integrations",
    icon: FileText,
    color: "text-emerald-400",
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/30",
    url: `${API}/downloads/documentation/pdf`,
    filename: "CMES_Documentation_Technique.pdf",
    format: "PDF",
  },
  {
    id: "guide-domaine",
    title: "Guide Achat Domaine",
    description: "Guide simplifie pour l'achat d'un nom de domaine et le deploiement en production",
    icon: Globe,
    color: "text-violet-400",
    bg: "bg-violet-500/15",
    border: "border-violet-500/30",
    url: `${API}/downloads/guide-domaine/pdf`,
    filename: "CMES_Guide_Achat_Domaine.pdf",
    format: "PDF",
  },
  {
    id: "code-source",
    title: "Code Source (Archive ZIP)",
    description: "Archive ZIP contenant tout le code frontend (React), backend (FastAPI) et la documentation",
    icon: FileDown,
    color: "text-amber-400",
    bg: "bg-amber-500/100/15",
    border: "border-amber-500/30",
    url: `${API}/downloads/code-source/zip`,
    filename: "CMES_PHARMA_INTEL_CODE_SOURCE.zip",
    format: "ZIP",
  },
];

const DownloadsPage = () => {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const res = await fetch(doc.url);
      if (!res.ok) throw new Error("Erreur de téléchargement");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${doc.title} téléchargé`);
    } catch {
      toast.error(`Erreur lors du téléchargement de ${doc.title}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6" data-testid="downloads-page">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-white flex items-center gap-3">
            <FileDown className="w-8 h-8 text-sky-400" />
            Documents & Téléchargements
          </h1>
          <p className="text-slate-400 mt-1">Tous les documents de la plateforme CMES INTEL-STOCK</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {DOCUMENTS.map((doc) => {
            const Icon = doc.icon;
            const isDownloading = downloading === doc.id;
            return (
              <Card key={doc.id} className={`glass-card border ${doc.border} hover:shadow-lg transition-shadow`}
                data-testid={`doc-card-${doc.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${doc.bg}`}>
                        <Icon className={`w-6 h-6 ${doc.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-white text-base">{doc.title}</CardTitle>
                        <Badge variant="outline" className={`mt-1 text-[10px] ${doc.color} ${doc.border}`}>
                          {doc.format}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-400 text-sm">{doc.description}</p>
                  <Button onClick={() => handleDownload(doc)} disabled={isDownloading}
                    className="w-full bg-slate-800 hover:bg-slate-700 text-white border border-white/10"
                    data-testid={`download-btn-${doc.id}`}>
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Télécharger
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info */}
        <Card className="glass-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <Gauge className="w-10 h-10 text-sky-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-white">Besoin d'aide ?</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Le <strong>Guide des Fonctionnalités</strong> est le document le plus complet — il couvre toutes les capacités
                  de la plateforme en 10 sections. Pour les développeurs, la <strong>Documentation Technique</strong> détaille
                  les endpoints API et l'architecture. Pour déployer votre propre instance, consultez le
                  <strong> Guide Achat Domaine</strong>.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DownloadsPage;
